"use client";

import { clsx } from "clsx";
import Image from "next/image";
import { useAnimationSequence } from "@ui/hooks/useAnimationSequence";
import { useRef, useEffect, useState } from "react";
import { Noise } from "@ui/components/core/Noise/Noise";

// Avatar component for agent avatar
export function Avatar({ src }: { src: string }) {
  return (
    <Image
      src={src}
      alt='Agent avatar'
      width={36}
      height={36}
      sizes='36px'
      className='tw-rounded-full tw-object-cover tw-flex-shrink-0'
    />
  );
}

// Speaking indicator that appears when agent is talking
export function SpeakingIndicator({ isVisible }: { isVisible: boolean }) {
  if (!isVisible) return null;

  return (
    <div
      className='tw-flex tw-items-center tw-gap-1 tw-text-gray-400 tw-text-[0.75rem]/5 tw-bg-white tw-rounded-tl-3xl tw-rounded-tr-3xl tw-rounded-bl-md tw-rounded-br-3xl tw-py-1 tw-px-2.5'
      style={{
        boxShadow: "0 0 0 0.5px rgb(0 0 0 / 0.08), 0 1.677px 3.354px 0 rgb(0 0 0 / 0.04)",
      }}
    >
      <span>Speaking...</span>
    </div>
  );
}

export function StaticBubble({
  sentences,
  variant = "agent",
  className,
}: {
  sentences: Array<string | Array<string>>;
  variant?: "agent" | "user";
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "tw-relative tw-isolate tw-max-w-56 tw-text-black tw-min-w-10 tw-min-h-10 tw-w-fit tw-py-2 tw-px-3 tw-text-[0.75rem]/5",
        variant === "agent" ? "" : "tw-ml-auto",
        className,
      )}
    >
      <div
        className={clsx("tw-absolute tw-inset-0 -tw-z-10", variant === "agent" ? "tw-bg-white" : "tw-bg-[#FAF9F8]")}
        style={{
          boxShadow:
            variant === "agent" ? "0 0 0 0.5px rgb(0 0 0 / 0.08), 0 1.677px 3.354px 0 rgb(0 0 0 / 0.04)" : undefined,
          borderRadius: 20,
        }}
      />
      {sentences.map((sentence, sentenceIndex) => {
        const words = Array.isArray(sentence) ? sentence : sentence.split(" ");
        return words.map((word, wordIndex) => (
          <div key={`${sentenceIndex}-${wordIndex}`} className='tw-inline'>
            {word}
            {Array.isArray(sentence) ? "" : " "}
          </div>
        ));
      })}
    </div>
  );
}

export function Bubble({
  step,
  sentences,
  m,
  variant = "agent",
  className,
  delay = 0,
}: {
  step: string;
  sentences: Array<string | Array<string>>;
  variant?: "agent" | "user";
  className?: string;
  delay?: number;
  m: any; // Using any to avoid type issues with different states arrays
}) {
  return (
    <div
      className={clsx(
        "tw-relative tw-isolate tw-max-w-56 tw-min-w-10 tw-text-black tw-min-h-10 tw-w-fit tw-py-2.5 tw-px-3.5 tw-text-[0.75rem]/5",
        variant === "agent" ? undefined : "tw-ml-auto",
        className,
      )}
    >
      <m.div
        className={clsx("tw-absolute tw-inset-0 -tw-z-10", variant === "agent" ? "tw-bg-white" : "tw-bg-[#FAF9F8]")}
        layout
        update={sentences.map((_, s) => `${step}_${s}`)}
        wait={false}
        style={{
          boxShadow:
            variant === "agent" ? "0 0 0 0.5px rgb(0 0 0 / 0.08), 0 1.677px 3.354px 0 rgb(0 0 0 / 0.04)" : undefined,
          borderRadius: 20,
        }}
        initial={{ opacity: 0 }}
        animate={{ [`${step}_0`]: { opacity: 1 } }}
        transition={{ [`${step}_0`]: { delay }, [`${step}_1`]: { delay: 0.5 } }}
      />
      {sentences.map((sentence, sentenceIndex) => {
        const words = Array.isArray(sentence) ? sentence : sentence.split(" ");
        return words.map((word, wordIndex) => (
          <m.div
            key={`${sentenceIndex}-${wordIndex}`}
            render={[`${step}_${sentenceIndex}`]}
            animatePresence={{ initial: false }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.1, delay: (sentenceIndex === 0 ? delay : 0.5) + 0.2 + wordIndex * 0.15 },
            }}
            className='tw-inline'
          >
            {word}
            {Array.isArray(sentence) ? "" : " "}
          </m.div>
        ));
      })}
    </div>
  );
}

// Container component that handles all common logic and layout
export function ChatGuardrailsContainer({
  backgroundImage,
  avatarSrc,
  states,
  agentSpeakingStates,
  children,
}: {
  backgroundImage: string;
  avatarSrc: string;
  states: string[];
  agentSpeakingStates: string[];
  children: (m: any) => React.ReactNode;
}) {
  const { motion: m, reset, start, store } = useAnimationSequence(states);

  const [key, setKey] = useState(0);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);

  useEffect(() => {
    // Auto-start animation when component mounts
    const timer = window.setTimeout(() => {
      setKey(1);
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (key === 0) return;
    reset();
    const timer = window.setTimeout(() => {
      start();
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [key, reset, start]);

  // Subscribe to animation store to control speaking indicator and restart logic
  useEffect(() => {
    const unsubscribe = store.subscribe(state => {
      const currentStateName = states[state.state];

      // Agent is speaking during AGENT states when animation is running
      const shouldBeAgentSpeaking = agentSpeakingStates.includes(currentStateName) && state.status === "running";

      // Use functional update to avoid needing isAgentSpeaking in dependencies
      setIsAgentSpeaking(prev => {
        if (prev !== shouldBeAgentSpeaking) {
          return shouldBeAgentSpeaking;
        }
        return prev;
      });

      // Handle restart logic
      if (state.status === "complete" && !restartTimeoutRef.current) {
        restartTimeoutRef.current = setTimeout(() => {
          setIsAgentSpeaking(false);
          setKey(k => k + 1);
          restartTimeoutRef.current = null;
        }, 3000);
      }
    });

    return () => {
      unsubscribe();
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
    };
  }, [store, states, agentSpeakingStates]);

  return (
    <div className='tw-h-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-p-4' inert>
      <div
        className='tw-relative tw-isolate tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center'
        style={{ borderRadius: 24 }}
      >
        {/* Background image */}
        <Image src={backgroundImage} alt='' fill className='tw-object-cover -tw-z-20' style={{ borderRadius: 24 }} />

        {/* Noise overlay at 50% opacity */}
        <Noise className='-tw-z-10' style={{ opacity: 0.5, borderRadius: 24 }} />

        {/* Chat container */}
        <div
          className='tw-max-w-80 tw-w-full tw-bg-white tw-min-h-[350px] tw-p-5'
          style={{
            boxShadow: "0 0 0 0.5px rgb(0 0 0 / 0.08), 0 1.677px 3.354px 0 rgb(0 0 0 / 0.04)",
            borderRadius: 20,
            maxWidth: "18rem",
          }}
        >
          <div className='tw-mb-5 tw-flex tw-items-center tw-gap-2'>
            <Avatar src={avatarSrc} />
            <SpeakingIndicator isVisible={isAgentSpeaking} />
          </div>

          <div className='tw-relative'>{children(m)}</div>
        </div>
      </div>
    </div>
  );
}
